import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getFeed, getFeedByUser } from "apis/PostServiceApi";
import { RootState } from "store/types";


export interface UiPost {
  postId: number;
  postOwnerId: number;
  organizationName: string;
  description: string;
  profilePictureURL: string;
  contentPicture: string;
  time: string;
  likesCount: number;
  isCurrentLiked: boolean;
  likesProfiles: { profilePictureURL: string; name: string }[];
  comments: number;
  shares: number;
}

interface FeedState {
  posts: UiPost[];
  feedType: "following" | "me";
  page: number;
  totalPages: number;
  hasMore: boolean;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: FeedState = {
  posts: [],
  feedType: "following",
  page: 1,
  totalPages: 1,
  hasMore: true,
  status: "idle",
  error: null,
};

export const fetchFeed = createAsyncThunk<
  { posts: UiPost[]; page: number; totalPages: number },
  { publicUserId?: number } | void,
  { state: RootState }
>("feed/fetchFeed", async (arg, { getState, rejectWithValue }) => {
  const { feed } = getState();
  const { feedType, page } = feed;
  const publicUserId = arg && "publicUserId" in arg ? arg.publicUserId : undefined;

  try {
    const res = publicUserId ? await getFeedByUser(publicUserId, page, 5) : await getFeed(feedType, page, 5);
    const { data: rawPosts, pagination } = res;

    const currentUserId = getState().auth.userId;

    const posts: UiPost[] = rawPosts.map((p: any) => ({
      postId: p.id,
      postOwnerId: p.user?.id,
      organizationName: p.user?.name || "Unknown Organization",
      description: p.post_caption || "",
      isCurrentLiked: p.likes.some(
        (l: any) => l.pivot?.user_id === currentUserId
      ),
      likesCount: p.likes.length,
      profilePictureURL: p.user?.socialAvatarUrl || "",
      contentPicture: p.images?.[0]?.image_url || "",
      time: p.created_at || "",
      likesProfiles: p.likes.map((l: any) => ({
        profilePictureURL: l.socialAvatarUrl || "",
        name: l.name || "Unknown",
      })),
      comments: p.commentsCount || 0,
      shares: 0,
    }));

    return {
      posts,
      page,
      totalPages: pagination.total_pages,
    };
  } catch (err: any) {
    return rejectWithValue(err.message ?? "Fetch error");
  }
});

const feedSlice = createSlice({
  name: "feed",
  initialState,
  reducers: {
    changeFeedType: (state, action: PayloadAction<"following" | "me">) => {
      if (state.feedType !== action.payload) {
        state.feedType = action.payload;
        state.posts = [];
        state.page = 1;
        state.totalPages = 1;
        state.hasMore = true;
        state.status = "idle";
      }
    },
    nextPage: (state) => {
      if (state.hasMore && state.status !== "loading") {
        state.page += 1;
      }
    },
    resetFeed: () => initialState,
    updateLikeStatus: (
      state,
      action: PayloadAction<{
        postId: number;
        userAvatar: string;
        isLiked: boolean;
      }>
    ) => {
      const { postId, userAvatar, isLiked } = action.payload;
      const post = state.posts.find((p) => p.postId === postId);
      if (post) {
        post.isCurrentLiked = isLiked;
        post.likesCount += isLiked ? 1 : -1;
        post.likesProfiles = isLiked
          ? [
              ...post.likesProfiles,
              { profilePictureURL: userAvatar, name: "Current User" },
            ]
          : post.likesProfiles.filter(
              (p) => p.profilePictureURL !== userAvatar
            );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeed.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchFeed.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.posts =
          action.payload.page === 1
            ? action.payload.posts
            : [...state.posts, ...action.payload.posts];
        state.totalPages = action.payload.totalPages;
        state.hasMore = state.page < state.totalPages;
      })
      .addCase(fetchFeed.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message as string;
        state.hasMore = false;
      });
  },
});

export const { changeFeedType, nextPage, resetFeed, updateLikeStatus } =
  feedSlice.actions;
export default feedSlice.reducer;

export const selectFeedPosts = (state: RootState) => state.feed.posts;
export const selectFeedHasMore = (state: RootState) => state.feed.hasMore;
export const selectFeedStatus = (state: RootState) => state.feed.status;
export const selectFeedPage = (state: RootState) => state.feed.page;
export const selectErrorPosts = (state: RootState) => state.feed.error;
